import api from '../utils/api';

/**
 * Service for managing food safety certificates
 */
const certificateService = {
  /**
   * Get all certificates for the authenticated user
   * @returns {Promise} - API response with certificates
   */
  getUserCertificates: async () => {
    try {
      const response = await api.get('/certificates');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch certificates' };
    }
  },

  /**
   * Get a certificate by ID
   * @param {string} id - Certificate ID
   * @returns {Promise} - API response with certificate details
   */
  getCertificateById: async (id) => {
    try {
      const response = await api.get(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch certificate details' };
    }
  },

  /**
   * Upload a new certificate (donor only)
   * @param {Object} formData - Certificate form data (must include file)
   * @returns {Promise} - API response
   */
  uploadCertificate: async (formData) => {
    try {
      // Use multipart/form-data for file uploads
      const response = await api.post('/certificates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to upload certificate' };
    }
  },

  /**
   * Update an existing certificate (donor only)
   * @param {string} id - Certificate ID
   * @param {Object} formData - Updated certificate data
   * @returns {Promise} - API response
   */
  updateCertificate: async (id, formData) => {
    try {
      // Use multipart/form-data for file uploads
      const response = await api.put(`/certificates/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update certificate' };
    }
  },

  /**
   * Delete a certificate (donor only)
   * @param {string} id - Certificate ID
   * @returns {Promise} - API response
   */
  deleteCertificate: async (id) => {
    try {
      const response = await api.delete(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete certificate' };
    }
  },

  /**
   * Check if user has a valid certificate
   * @returns {Promise} - Resolves to boolean indicating if user has valid certificate
   */
  hasValidCertificate: async () => {
    try {
      // First check if the user's email_verified status is true
      try {
        const profile = await api.get('/auth/profile');
        if (profile?.data?.user?.email_verified === true) {
          return true;
        }
      } catch (profileError) {
        console.log('Could not fetch profile, falling back to certificates check', profileError);
        // Continue to certificate check
      }
      
      // Fall back to checking certificates directly
      const response = await certificateService.getUserCertificates();
      
      // Extract certificates array from the nested structure
      let certificatesArray = [];
      if (response?.certificates?.results && Array.isArray(response.certificates.results)) {
        // New format: certificates is an object with results array
        certificatesArray = response.certificates.results;
      } else if (response?.certificates && Array.isArray(response.certificates)) {
        // Old format: certificates is an array directly
        certificatesArray = response.certificates;
      }
      
      console.log('Processing certificates array:', certificatesArray);
      
      // Check for any valid certificate
      return certificatesArray.some(cert => {
        const isApproved = cert.status === 'approved';
        const isValid = new Date(cert.expiry_date) > new Date();
        console.log(`Certificate ${cert.id}: status=${cert.status}, expiry=${cert.expiry_date}, valid=${isValid && isApproved}`);
        return isApproved && isValid;
      });
    } catch (error) {
      console.error('Error checking certificate validity:', error);
      return false;
    }
  },

  /**
   * Approve a certificate (admin only)
   * @param {string} id - Certificate ID
   * @returns {Promise} - API response
   */
  approveCertificate: async (id) => {
    try {
      const response = await api.put(`/certificates/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to approve certificate' };
    }
  },

  /**
   * Reject a certificate (admin only)
   * @param {string} id - Certificate ID
   * @param {Object} data - Rejection reason
   * @returns {Promise} - API response
   */
  rejectCertificate: async (id, data) => {
    try {
      const response = await api.put(`/certificates/${id}/reject`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reject certificate' };
    }
  }
};

export default certificateService;
